import React from 'react';
import { useTranslation } from 'react-i18next';
import { X, Download, Share2, QrCode, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { saveAs } from 'file-saver';

export default function ShovarPreviewModal({ isOpen, onClose, shovarData }) {
  const { i18n } = useTranslation();
  
  if (!isOpen || !shovarData) return null;

  const handleDownload = () => {
    try {
      // Method 1: Use file-saver library (most reliable)
      if (shovarData.pdfBlob) {
        saveAs(shovarData.pdfBlob, shovarData.fileName);
        return;
      }
      
      // Method 2: Fallback to jsPDF save
      if (shovarData.doc) {
        shovarData.doc.save(shovarData.fileName);
        return;
      }
    } catch (e) {
      console.error('Download error:', e);
      alert('Download failed. Please try the Share button instead.');
    }
  };

  const handleShare = async () => {
    if (navigator.share && shovarData.pdfBlob) {
      try {
        const file = new File([shovarData.pdfBlob], shovarData.fileName, { type: 'application/pdf' });
        await navigator.share({
          title: 'SHALEM Shovar',
          text: `Shovar ${shovarData.shovarNumber}`,
          files: [file]
        });
      } catch (err) {
        handleDownload();
      }
    } else {
      handleDownload();
    }
  };

  const labels = {
    en: {
      title: 'Payment Voucher Ready!',
      download: 'DOWNLOAD PDF',
      share: 'Share',
      shovarNumber: 'Shovar Number',
      instructions: 'Present this at any Post Office or Bank to pay in cash',
      clickDownload: 'Tap the button above to download',
      provider: 'Provider',
      amount: 'Amount to Pay',
      dueDate: 'Due Date',
      payer: 'Payer'
    },
    he: {
      title: 'שובר התשלום מוכן!',
      download: 'הורד PDF',
      share: 'שתף',
      shovarNumber: 'מספר שובר',
      instructions: 'הצג שובר זה בכל סניף דואר או בנק לתשלום במזומן',
      clickDownload: 'לחץ על הכפתור למעלה להורדה',
      provider: 'ספק',
      amount: 'סכום לתשלום',
      dueDate: 'תאריך פירעון',
      payer: 'משלם'
    },
    fr: {
      title: 'Bon de paiement prêt!',
      download: 'TÉLÉCHARGER PDF',
      share: 'Partager',
      shovarNumber: 'Numéro du bon',
      instructions: 'Présentez ce bon à la poste ou banque pour payer en espèces',
      clickDownload: 'Appuyez sur le bouton pour télécharger',
      provider: 'Fournisseur',
      amount: 'Montant à payer',
      dueDate: 'Date d\'échéance',
      payer: 'Payeur'
    }
  };

  const l = labels[i18n.language] || labels.en;
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto">
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 bg-white/10 hover:bg-white/20 text-white p-2 rounded-full transition-colors"
        data-testid="close-shovar-modal"
      >
        <X className="h-6 w-6" />
      </button>

      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden my-4">
        {/* Success Header */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-6 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
            <CheckCircle className="h-10 w-10" />
          </div>
          <h2 className="text-2xl font-bold">{l.title}</h2>
        </div>

        {/* Shovar Preview */}
        <div className="p-4">
          <div className="border-2 border-slate-200 rounded-2xl p-4 bg-gradient-to-b from-white to-slate-50">
            {/* Mini Shovar Header */}
            <div className="bg-blue-600 text-white text-center py-3 rounded-xl mb-4">
              <p className="text-lg font-bold">SHALEM</p>
              <p className="text-xs opacity-80">שובר תשלום / Payment Voucher</p>
            </div>

            {/* Shovar Number */}
            <div className="text-center mb-4 p-3 bg-blue-50 rounded-xl border border-blue-100">
              <p className="text-xs text-slate-500">{l.shovarNumber}</p>
              <p className="text-xl font-mono font-black text-blue-600">
                {shovarData.shovarNumber}
              </p>
            </div>

            {/* Details */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between p-2 bg-slate-50 rounded-lg">
                <span className="text-slate-500">{l.payer}:</span>
                <span className="font-semibold">{user.full_name || 'Demo User'}</span>
              </div>
              <div className="flex justify-between p-2 bg-slate-50 rounded-lg">
                <span className="text-slate-500">{l.provider}:</span>
                <span className="font-semibold text-right">{shovarData.bill?.provider || 'N/A'}</span>
              </div>
              <div className="flex justify-between p-2 bg-slate-50 rounded-lg">
                <span className="text-slate-500">{l.dueDate}:</span>
                <span className="font-semibold">
                  {shovarData.bill?.due_date ? new Date(shovarData.bill.due_date).toLocaleDateString() : 'N/A'}
                </span>
              </div>
            </div>

            {/* Amount */}
            <div className="mt-4 bg-blue-600 text-white text-center py-4 rounded-xl">
              <p className="text-xs opacity-80">{l.amount}</p>
              <p className="text-3xl font-bold">₪{shovarData.bill?.amount?.toFixed(2) || '0.00'}</p>
            </div>

            {/* QR Code indicator */}
            <div className="mt-4 flex items-center justify-center gap-2 text-green-600 bg-green-50 p-2 rounded-lg">
              <QrCode className="h-5 w-5" />
              <span className="text-sm font-medium">QR Code in PDF ✓</span>
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="px-4 pb-2">
          <p className="text-center text-amber-700 bg-amber-50 p-3 rounded-xl text-sm font-medium">
            📄 {l.instructions}
          </p>
        </div>

        {/* Download Button - BIG and obvious */}
        <div className="p-4 space-y-3">
          <Button
            onClick={handleDownload}
            className="w-full h-16 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl shadow-lg text-xl animate-pulse"
            data-testid="download-shovar-button"
          >
            <Download className="h-7 w-7 mr-3" />
            {l.download}
          </Button>
          
          <Button
            onClick={handleShare}
            variant="outline"
            className="w-full h-12 border-2 font-semibold rounded-xl"
            data-testid="share-shovar-button"
          >
            <Share2 className="h-5 w-5 mr-2" />
            {l.share}
          </Button>
          
          <p className="text-center text-sm text-slate-500 font-medium">
            👆 {l.clickDownload}
          </p>
        </div>
      </div>
    </div>
  );
}
